import React, { Component, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { SlateDialog } from 'components/Shared/SlateDialog';
import { DEFICIENCY_ACTIONS } from 'store/actions/inspect';
import { getImageLink } from 'helpers/imageHelper';
import {
  markerBadgeText,
  resultLabelText,
} from 'components/Inspect/resultLabels';
import { imageSizes } from 'config/constants';

// The timeline endpoint (spec section 7) already returns an array. This
// stays a defensive normalizer -- and stays exported, since callers that
// have not made the round trip yet (tests, a raw JSON string) still pass
// through it -- rather than assuming the prop is always already an array.
export function parseActivityLog(raw) {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string' || raw === '') return [];
  try {
    var parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

export var ACTION_COPY = {};
ACTION_COPY[DEFICIENCY_ACTIONS.MARK_DEFICIENT] = {
  title: 'inspect:deficiencyMarkDeficient',
  titleFallback: 'Mark as Failed',
  noteLabel: 'inspect:deficiencyNoteReason',
  noteFallback: 'Reason',
  noteRequired: true,
  icon: 'flag',
};
ACTION_COPY[DEFICIENCY_ACTIONS.CLOSE_OUT] = {
  title: 'inspect:deficiencyCloseOut',
  titleFallback: 'Mark Corrected',
  noteLabel: 'inspect:deficiencyNoteCorrectiveAction',
  noteFallback: 'Corrective Action Taken',
  noteRequired: true,
  icon: 'task_alt',
};
ACTION_COPY[DEFICIENCY_ACTIONS.REVERT] = {
  title: 'inspect:deficiencyRevert',
  titleFallback: 'Revert to Computed',
  noteLabel: 'inspect:deficiencyNoteOptional',
  noteFallback: 'Note (Optional)',
  noteRequired: false,
  icon: 'undo',
};

// The header label for one timeline row. The four kinds InspectionService
// itself contributes (completed/amended/voided/photoResolved) are checked
// first and explicitly, so none of them can fall through to the pass/fail/
// incomplete guess below (a voided entry carries no result at all, and would
// otherwise silently read as "Incomplete"). Everything below that line is
// the engine/deficiency log shape; anything not named there is an automatic
// engine evaluation (see isEngineResultEntry).
var NAMED_ACTIVITY_KINDS = {
  completed: true,
  amended: true,
  voided: true,
  photoResolved: true,
  overridden: true,
  closed: true,
  reverted: true,
};

// True for an automatic engine evaluation row: kind 'evaluated' (the API's
// LogKindEvaluated), or any other kind activityLabel does not name, which is
// exactly the rows its pass/fail/incomplete fallback labels. Identified by
// kind, never by the actor name.
export function isEngineResultEntry(entry) {
  var kind = entry && entry.kind;
  return !NAMED_ACTIVITY_KINDS[kind];
}

// Rows the system wrote rather than a person: engine evaluations and a
// resolved deferred photo. They show the SlateMate wordmark instead of an
// actor. Mirrors the app's isSystemActivityKind.
export function isSystemActivityEntry(entry) {
  return isEngineResultEntry(entry) || (entry && entry.kind) === 'photoResolved';
}

export function activityLabel(entry, t) {
  var kind = entry && entry.kind;
  if (kind === 'completed') return t('inspect:activityCompleted', 'Completed');
  if (kind === 'amended') return t('inspect:activityAmended', 'Amended');
  if (kind === 'voided') return t('inspect:activityVoided', 'Voided');
  if (kind === 'photoResolved')
    return t('inspect:activityPhotoResolved', 'Photo Resolved');
  if (kind === 'overridden')
    return t('inspect:logMarkedDeficient', 'Marked as Failed');
  if (kind === 'closed') return t('inspect:logCorrected', 'Corrected');
  if (kind === 'reverted')
    return t('inspect:logReverted', 'Reverted to Computed');
  return t('inspect:logResult', 'Result');
}

// A timeline row's heading chips: the state its event produced, in the
// History column's own chip vocabulary (resultChipInfo in InspectRecords.js)
// and the app's InspectionResultChip -- a result chip, then any marker
// chips. Each is { className, label }: className goes after
// .inspect-result-badge, label through activityBadgeText. Empty for an event
// that leaves no chip state behind (completed, photoResolved), and for a
// reverted row with no result, which keep their plain label instead.
function resultBadge(raw) {
  var result = (raw || '').toLowerCase();
  var label = result === 'pass' || result === 'fail' ? result : 'incomplete';
  return { className: label, label: label };
}

// Paired with the verb detail line for a row whose chips name only the
// state it produced -- null where the chip says it (Void, Corrected,
// Amended), where the heading is already the verb, and for an engine row
// (it shows the SlateMate wordmark as its actor) -- so the two
// can never disagree about a kind. Returns { badges, verbDetail }.
export function activityHeading(entry, t) {
  var kind = entry && entry.kind;
  if (isEngineResultEntry(entry)) {
    return { badges: [resultBadge(entry && entry.result)], verbDetail: null };
  }
  if (kind === 'voided') {
    return { badges: [{ className: 'void', label: 'void' }], verbDetail: null };
  }
  if (kind === 'closed') {
    return {
      badges: [{ className: 'corrected', label: 'corrected' }],
      verbDetail: null,
    };
  }
  if (kind === 'amended') {
    return {
      badges: [{ className: 'marker', label: 'amended' }],
      verbDetail: null,
    };
  }
  if (kind === 'overridden') {
    return {
      badges: [resultBadge('fail'), { className: 'marker', label: 'manual' }],
      verbDetail: t('inspect:logMarkedDeficientDetail', 'Marked as failed'),
    };
  }
  if (kind === 'reverted' && entry.result) {
    return {
      badges: [resultBadge(entry.result)],
      verbDetail: t('inspect:logRevertedDetail', 'Reverted to computed result'),
    };
  }
  return { badges: [], verbDetail: null };
}

export function activityBadgeText(badge, t) {
  return badge.className === 'marker'
    ? markerBadgeText(badge.label, t)
    : resultLabelText(badge.label, t);
}

// "Sep 21, 8:02 am", or "Sep 21" without withTime; the year is added when it
// is not the current one. `at` is anything parseEntryTimestamp accepts or a
// moment. Always the viewer's local time. Ports the app's
// formatInspectionTimestamp.
export function formatActivityTimestamp(at, withTime) {
  var when = moment.isMoment(at) ? at : parseEntryTimestamp(at);
  if (!when.isValid()) return '';
  var date = when.format(
    when.year() === moment().year() ? 'MMM D' : 'MMM D, YYYY',
  );
  return withTime ? date + ', ' + when.format('h:mm a') : date;
}

// Format rules for one amendment change's before/after value (the app's
// formatInspectionChangeValue): empty as "(blank)", booleans as Yes/No,
// arrays joined (each entry formatted the same way), objects JSON.stringify,
// ISO dates as a readable date. Recurses through arrays so a gallery/
// multi-select value formats each entry the same way before joining.
export function formatChangeValue(value) {
  if (value === null || value === undefined || value === '') return '(blank)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    return value.length === 0
      ? '(blank)'
      : value.map(formatChangeValue).join(', ');
  }
  // moment.ISO_8601 alone also accepts a bare year ("2026") or basic-format
  // date ("20260905") -- too loose for an inspection answer, where a plain
  // 4-digit text value is plausible. Require at least a full calendar date
  // (YYYY-MM-DD) before trusting the ISO parse.
  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}/.test(value) &&
    moment(value, moment.ISO_8601, true).isValid()
  ) {
    // A bare calendar date has no time and no zone: show it as written,
    // never shifted to the viewer's zone or given a midnight time.
    if (value.length === 10) {
      return formatActivityTimestamp(moment(value, 'YYYY-MM-DD'), false);
    }
    return formatActivityTimestamp(moment(value), true);
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// One amendment change line: "<fieldPath>: <before> to <after>", or
// "<fieldPath>: changed" when the API truncated the values. fieldPath
// is "<group title> / <field title>" (InspectionService+SaveEvaluation.cs
// FieldPathFor) -- a human-readable label, not a machine path -- and is what
// the brief names; fieldTitle is a defensive fallback for a change that
// somehow carries no path. A change that registers a deferred edit (a photo
// placeholder, section 4 rule 6) has no before/after to show, so it renders
// the registration string instead. NOTE: the timeline API does not currently
// project `deferredEdit` onto a wire change (see the report) -- this branch
// is exercised here with a synthetic fixture so the dashboard is ready the
// day that gap closes, but no live change can hit it yet.
export function formatAmendmentChange(change, t) {
  var label = (change && (change.fieldPath || change.fieldTitle)) || '';
  if (change && change.deferredEdit) {
    return (
      label +
      ': ' +
      t('inspect:activityDeferredEditRegistered', 'Photo pending')
    );
  }
  if (change && change.truncated) return label + ': changed';
  var before = formatChangeValue(change ? change.before : undefined);
  var after = formatChangeValue(change ? change.after : undefined);
  return label + ': ' + before + ' to ' + after;
}

// True when an entry carries both results and they differ (case
// insensitive): the app's _resultMoved.
export function resultMoved(entry) {
  return (
    !!entry &&
    entry.resultBefore !== null &&
    entry.resultBefore !== undefined &&
    entry.result !== null &&
    entry.result !== undefined &&
    String(entry.resultBefore).toLowerCase() !==
      String(entry.result).toLowerCase()
  );
}

// "Pass" / "Fail" / "Incomplete"; "(blank)" for a missing or unrecognised
// result.
function resultWord(raw) {
  var key = String(raw === null || raw === undefined ? '' : raw).toLowerCase();
  if (key === 'pass') return 'Pass';
  if (key === 'fail') return 'Fail';
  if (key === 'incomplete') return 'Incomplete';
  return '(blank)';
}

// "Result: <before> to <after>", shown on any non-engine row whose result
// moved (see resultMoved).
export function formatResultChange(resultBefore, result, t) {
  return t('inspect:activityResultChange', 'Result: {before} to {after}')
    .replace('{before}', resultWord(resultBefore))
    .replace('{after}', resultWord(result));
}

export class DeficiencyNoteDialog extends Component {
  constructor(props) {
    super(props);
    this.state = { note: '', submitting: false, error: null };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.open && !this.props.open) {
      this.setState({ note: '', submitting: false, error: null });
    }
  }

  handleChange(e) {
    this.setState({ note: e.target.value, error: null });
  }

  handleClose() {
    if (this.state.submitting) return;
    this.props.onClose();
  }

  handleSubmit() {
    var self = this;
    var note = this.state.note.trim();
    this.setState({ submitting: true, error: null });
    this.props
      .onSubmit(note)
      .then(function () {
        self.setState({ submitting: false });
        self.props.onClose();
      })
      .catch(function (err) {
        self.setState({
          submitting: false,
          error: (err && err.message) || self.props.t('messages:genericError'),
        });
      });
  }

  render() {
    var props = this.props;
    var copy = ACTION_COPY[props.action] || ACTION_COPY.closeOut;
    var t = props.t;
    var title = t(copy.title, copy.titleFallback);
    var canSubmit =
      !this.state.submitting &&
      (!copy.noteRequired || this.state.note.trim() !== '');
    return (
      <SlateDialog
        open={props.open}
        onClose={this.handleClose}
        title={title}
        maxWidth="xs"
        actions={[
          {
            key: 'cancel',
            label: t('inspect:deficiencyCancel', 'Cancel'),
            variant: 'secondary',
            onClick: this.handleClose,
            disabled: this.state.submitting,
          },
          {
            key: 'confirm',
            label: title,
            variant: 'primary',
            onClick: this.handleSubmit,
            disabled: !canSubmit,
            loading: this.state.submitting,
          },
        ]}
      >
        <label className="inspect-note-label" htmlFor="inspect-deficiency-note">
          {t(copy.noteLabel, copy.noteFallback)}
        </label>
        <textarea
          id="inspect-deficiency-note"
          className="inspect-note-textarea"
          value={this.state.note}
          onChange={this.handleChange}
          rows={4}
          disabled={this.state.submitting}
        />
        {this.state.error && (
          <div className="auth-form-error">{this.state.error}</div>
        )}
      </SlateDialog>
    );
  }
}

DeficiencyNoteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  action: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export var VOID_REASON_MAX_LENGTH = 500;

// One void-preview date (epoch seconds, or null for "none") in the format its
// tab already uses: Next Due is date-only like the Scheduled due column, Last
// Inspected carries the time like the History timestamp column. Null and
// unparseable values render emptyLabel ("None" in the dialog).
export function formatVoidPreviewDate(value, format, emptyLabel) {
  var empty = emptyLabel || '-';
  if (value === null || value === undefined || value === '') return empty;
  var when = parseEntryTimestamp(value);
  return when.isValid() ? when.format(format) : empty;
}

// The schedule-effect lines for a void-preview resource: before -> after for
// Next Due and Last Inspected, or the single no-change line when the commit
// would write nothing (`changes` false -- the API computes that with the
// commit's own write gate, so it is trusted over comparing the dates here).
export function voidPreviewLines(preview, t) {
  if (!preview || !preview.changes) {
    return [
      t(
        'inspect:voidPreviewNoChange',
        "Voiding does not change this asset's schedule.",
      ),
    ];
  }
  var none = t('inspect:voidPreviewNone', 'None');
  return [
    t('inspect:voidPreviewNextDue', 'Next Due: {before} -> {after}')
      .replace(
        '{before}',
        formatVoidPreviewDate(preview.nextDueBefore, 'MM/DD/YYYY', none),
      )
      .replace(
        '{after}',
        formatVoidPreviewDate(preview.nextDueAfter, 'MM/DD/YYYY', none),
      ),
    t('inspect:voidPreviewLastInspected', 'Last Inspected: {before} -> {after}')
      .replace(
        '{before}',
        formatVoidPreviewDate(
          preview.lastInspectedBefore,
          'MM/DD/YYYY h:mm a',
          none,
        ),
      )
      .replace(
        '{after}',
        formatVoidPreviewDate(
          preview.lastInspectedAfter,
          'MM/DD/YYYY h:mm a',
          none,
        ),
      ),
  ];
}

// Void Inspection (spec section 6; dashboard-only). Fetches the schedule
// preview each time it opens (onPreview resolves to the void-preview
// resource) and submits the trimmed reason through onSubmit, which resolves
// on success and rejects with a display-safe message otherwise.
export class VoidInspectionDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reason: '',
      submitting: false,
      error: null,
      preview: null,
      previewLoading: false,
      previewError: null,
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClose = this.handleClose.bind(this);
    // Bumped on every open and on unmount; a preview response only lands if
    // it still matches, so a slow preview for a previous row cannot overwrite
    // the current one (the same token-compare InspectRecords' View Log uses).
    this._previewRequestId = 0;
  }

  componentDidMount() {
    if (this.props.open) this.loadPreview();
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      this.loadPreview();
    } else if (prevProps.open && !this.props.open) {
      this._previewRequestId += 1;
      this.setState({ reason: '', submitting: false, error: null });
    }
  }

  componentWillUnmount() {
    this._previewRequestId += 1;
  }

  loadPreview() {
    this._previewRequestId += 1;
    var requestId = this._previewRequestId;
    this.setState({ preview: null, previewLoading: true, previewError: null });
    if (typeof this.props.onPreview !== 'function') {
      this.setState({ previewLoading: false });
      return;
    }
    var self = this;
    this.props.onPreview().then(
      function (preview) {
        if (requestId !== self._previewRequestId) return;
        self.setState({ preview: preview || null, previewLoading: false });
      },
      function (err) {
        if (requestId !== self._previewRequestId) return;
        self.setState({
          previewLoading: false,
          previewError:
            (err && err.message) || self.props.t('messages:genericError'),
        });
      },
    );
  }

  handleChange(e) {
    this.setState({ reason: e.target.value, error: null });
  }

  handleClose() {
    if (this.state.submitting) return;
    this.props.onClose();
  }

  handleSubmit() {
    var self = this;
    var reason = this.state.reason.trim();
    if (reason === '' || this.state.submitting) return;
    this.setState({ submitting: true, error: null });
    this.props
      .onSubmit(reason)
      .then(function () {
        self.setState({ submitting: false });
        self.props.onClose();
      })
      .catch(function (err) {
        self.setState({
          submitting: false,
          error: (err && err.message) || self.props.t('messages:genericError'),
        });
      });
  }

  // The schedule effect, boxed with the .inspect-due-banner recipe. A preview
  // failure (e.g. the entry is no longer completed) shows in the error box
  // instead, since it is also why the void itself would be refused.
  renderPreview() {
    var t = this.props.t;
    if (this.state.previewError) {
      return <div className="auth-form-error">{this.state.previewError}</div>;
    }
    var lines;
    if (this.state.previewLoading) {
      lines = [
        t('inspect:voidPreviewLoading', 'Checking the effect on the schedule…'),
      ];
    } else if (this.state.preview) {
      lines = voidPreviewLines(this.state.preview, t);
    } else {
      return null;
    }
    return (
      <div className="inspect-void-preview">
        {lines.map(function (line, i) {
          return (
            <div className="inspect-void-preview-line" key={i}>
              {line}
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    var t = this.props.t;
    var title = t('inspect:voidInspection', 'Void Inspection');
    var canSubmit = !this.state.submitting && this.state.reason.trim() !== '';
    return (
      <SlateDialog
        open={this.props.open}
        onClose={this.handleClose}
        title={title}
        maxWidth="xs"
        actions={[
          {
            key: 'cancel',
            label: t('inspect:deficiencyCancel', 'Cancel'),
            variant: 'secondary',
            onClick: this.handleClose,
            disabled: this.state.submitting,
          },
          {
            key: 'confirm',
            label: title,
            variant: 'destructive',
            onClick: this.handleSubmit,
            disabled: !canSubmit,
            loading: this.state.submitting,
          },
        ]}
      >
        <div className="inspect-summary-info-body">
          {t(
            'inspect:voidInspectionBody',
            'The inspection stays in History, marked Void, but no longer counts as a completion for this asset. This cannot be undone.',
          )}
        </div>
        <div className="inspect-note-label">
          {t('inspect:voidPreviewHeading', "Effect on This Asset's Schedule")}
        </div>
        {this.renderPreview()}
        <label className="inspect-note-label" htmlFor="inspect-void-reason">
          {t('inspect:voidReason', 'Reason')}
        </label>
        <textarea
          id="inspect-void-reason"
          className="inspect-note-textarea"
          value={this.state.reason}
          onChange={this.handleChange}
          rows={4}
          maxLength={VOID_REASON_MAX_LENGTH}
          disabled={this.state.submitting}
        />
        {this.state.error && (
          <div className="auth-form-error">{this.state.error}</div>
        )}
      </SlateDialog>
    );
  }
}

VoidInspectionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onPreview: PropTypes.func,
  t: PropTypes.func.isRequired,
};

// The API sends every timestamp, `at` included, as epoch SECONDS (a double),
// never milliseconds -- unlike some history-row fields elsewhere in Inspect
// that carry either shape. moment.unix() is exact for that; moment() alone
// would read it as milliseconds and land in 1970.
function parseEntryTimestamp(at) {
  return typeof at === 'number' ? moment.unix(at) : moment(at);
}

// The actor avatar for a named (non-engine) row: the profile photo behind
// byImageUUID, via the same getImageLink helper Topbar/SlateMate use to turn
// an image UUID into a URL, or the initial-letter placeholder they fall back
// to when there is no photo -- reusing Topbar's avatarDefault tokens
// (--sl-avatar-default-*) so the fallback matches the rest of the dashboard.
// byImageUUID is null until the API ships it (added in parallel to this
// dialog); every existing fixture exercises the placeholder branch. A real
// component (not a plain render helper) so it can track a failed image load
// in state and fall back to the same placeholder rather than a broken image
// icon -- a stale or since-deleted image UUID should not leave a blank box.
function ActivityAvatar(props) {
  var entry = props.entry;
  var imagesLinks = props.imagesLinks;
  var name = (entry && entry.byName) || '';
  var initial = name ? name[0].toUpperCase() : '';
  var link =
    entry && entry.byImageUUID
      ? getImageLink(entry.byImageUUID, imagesLinks, imageSizes.ACTIVITY_AVATAR)
      : null;
  var _failed = useState(false);
  var failed = _failed[0];
  var setFailed = _failed[1];
  if (!link || failed) {
    return <span className="inspect-log-avatar-placeholder">{initial}</span>;
  }
  return (
    <img
      className="inspect-log-avatar"
      src={link}
      alt=""
      onError={function () {
        setFailed(true);
      }}
    />
  );
}

// The extra lines under an entry's header, kind-specific: the note (the log
// entry's note, the amendment's reason, the void's reason -- one flat `note`
// field for all three, so this is unconditional and kind-agnostic); for a
// backfilled completion, the creation actor and last editor the migration
// recovered (spec section 8; entry.source is only present when backfilled);
// for an amendment, its changes (one line per formatAmendmentChange) and the
// result move; for a resolved deferred photo, the same result move (the
// "cause" is the note above -- BuildResolutionOps already writes a
// descriptive one, see the report).
function renderEntryBody(entry, t) {
  var kind = entry && entry.kind;
  var body = [];
  if (entry && entry.note) {
    body.push(
      <div className="inspect-log-note" key="note">
        {entry.note}
      </div>,
    );
  }
  if (kind === 'completed' && entry.source) {
    if (entry.creationActorName) {
      body.push(
        <div className="inspect-log-note" key="creation-actor">
          {t('inspect:activityCreationActor', 'Created by {name}').replace(
            '{name}',
            entry.creationActorName,
          )}
        </div>,
      );
    }
    if (entry.lastEditorAtBackfillName) {
      body.push(
        <div className="inspect-log-note" key="last-editor">
          {t(
            'inspect:activityLastEditorAtBackfill',
            'Last edited by {name} before this was tracked',
          ).replace('{name}', entry.lastEditorAtBackfillName)}
        </div>,
      );
    }
  }
  (entry && Array.isArray(entry.changes) ? entry.changes : []).forEach(function (change, ci) {
    body.push(
      <div className="inspect-log-note" key={'change-' + ci}>
        {formatAmendmentChange(change, t)}
      </div>,
    );
  });
  if (!isEngineResultEntry(entry) && resultMoved(entry)) {
    body.push(
      <div className="inspect-log-note" key="result-change">
        {formatResultChange(entry.resultBefore, entry.result, t)}
      </div>,
    );
  }
  return body;
}

// Sort key for one timeline entry: its `at` as epoch seconds (a number, or
// anything moment can parse), or -Infinity when missing/unparseable so such
// an entry sinks to the bottom rather than breaking the order.
function entryTime(entry) {
  var at = entry && entry.at;
  // moment(undefined) is "now", which would float a dateless entry to the top.
  if (at === null || at === undefined || at === '') return -Infinity;
  var when = parseEntryTimestamp(at);
  return when.isValid() ? when.valueOf() : -Infinity;
}

// The timeline newest-first. The API delivers it ascending by `at`; this
// sorts explicitly rather than just reversing, and for entries sharing a
// timestamp puts the later-delivered one first -- the same order a plain
// reverse of the ascending list would give.
export function newestFirst(entries) {
  return entries
    .map(function (entry, index) {
      return { entry: entry, index: index, time: entryTime(entry) };
    })
    .sort(function (a, b) {
      if (a.time !== b.time) return a.time < b.time ? 1 : -1;
      return b.index - a.index;
    })
    .map(function (item) {
      return item.entry;
    });
}

// Reads an entry's full timeline (spec section 7): the engine/deficiency
// log, the completion, each amendment and the void -- newest at the top,
// going back in time down the list, which scrolls inside the dialog
// (.inspect-log) while the title bar stays put. InspectRecords fetches this
// live (View Log no longer reads the dropped activityLog column), so
// open/loading/error are all driven from there.
//
// Rendered as a vertical timeline (a rail connecting each row's dot, see
// style.css .inspect-log-row/.inspect-log-dot): only the newest row's dot is
// colored, in currentResultClassName -- the CURRENT result of the History
// row this dialog was opened for (InspectRecords' resultChipInfo), not any
// individual entry's own result -- so it matches that row's own chip. Every
// older dot stays neutral. A system row shows the SlateMate wordmark instead
// of a person; every other row shows its actor as an avatar (imagesLinks
// resolves byImageUUID through getImageLink, the same helper every other
// avatar in the dashboard uses) plus name.
export function ActivityLogDialog(props) {
  var t = props.t;
  var entries = newestFirst(parseActivityLog(props.entries));
  var imagesLinks = props.imagesLinks || {};
  var currentResultClassName = props.currentResultClassName;
  return (
    <SlateDialog
      open={props.open}
      onClose={props.onClose}
      title={t('inspect:activityLogTitle', 'Activity Log')}
      maxWidth="sm"
    >
      {props.loading ? (
        <div className="inspect-log-skeleton">
          {[0, 1, 2].map(function (i) {
            var odd = i % 2 === 1;
            return (
              <div key={i} className="inspect-log-skeleton-row">
                <span className="inspect-log-skeleton-dot" />
                <div className="inspect-log-skeleton-body">
                  <div className="inspect-log-skeleton-line">
                    <span
                      className="inspect-log-skeleton-bar inspect-log-skeleton-title"
                      style={{ width: odd ? 110 : 150 }}
                    />
                    <span
                      className="inspect-log-skeleton-bar inspect-log-skeleton-time"
                    />
                  </div>
                  <span
                    className="inspect-log-skeleton-bar inspect-log-skeleton-detail"
                    style={{ width: odd ? 170 : 220 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : props.error ? (
        <div className="inspect-log-message">{props.error}</div>
      ) : entries.length === 0 ? (
        <div className="inspect-log-message">
          {t('inspect:activityLogEmpty', 'No activity yet.')}
        </div>
      ) : (
        <div className="inspect-log">
          {entries.map(function (entry, i) {
            var when = parseEntryTimestamp(entry.at);
            var isSystem = isSystemActivityEntry(entry);
            var heading = activityHeading(entry, t);
            var badges = heading.badges;
            var verbDetail = heading.verbDetail;
            var actorText = (entry && entry.byName) || '';
            var isNewest = i === 0;
            var dotClassName =
              'inspect-log-dot' +
              (isNewest && currentResultClassName
                ? ' now ' + currentResultClassName
                : '');
            // Heading items: the chips, or the plain label when there are none.
            var items =
              badges.length === 0
                ? [
                    <span key="kind" className="inspect-log-kind">
                      {activityLabel(entry, t)}
                    </span>,
                  ]
                : badges.map(function (badge) {
                    return (
                      <span
                        key={badge.label}
                        className={'inspect-result-badge ' + badge.className}
                      >
                        {activityBadgeText(badge, t)}
                      </span>
                    );
                  });
            var byUnit = null;
            if (isSystem || actorText) {
              byUnit = (
                <span
                  key="by"
                  className={
                    'inspect-log-by' +
                    (badges.length === 0 ? ' after-label' : ' after-chip')
                  }
                >
                  {t('inspect:activityBy', 'by')}
                </span>
              );
            }
            var actor = null;
            if (isSystem) {
              actor = (
                <span key="actor" className="inspect-log-actor">
                  <img
                    className="slatemate-logo slatemate-logo--light inspect-log-logo"
                    src="/images/SlateMate-Logo.svg"
                    alt="SlateMate"
                  />
                  <img
                    className="slatemate-logo slatemate-logo--dark inspect-log-logo"
                    src="/images/SlateMate-Logo-DarkMode.svg"
                    alt="SlateMate"
                  />
                </span>
              );
            } else if (actorText) {
              actor = (
                <span key="actor" className="inspect-log-actor">
                  <ActivityAvatar entry={entry} imagesLinks={imagesLinks} />
                  <span className="inspect-log-actor-name">{actorText}</span>
                </span>
              );
            }
            var headingChildren = items.slice(0, items.length - 1);
            var lastItem = items[items.length - 1];
            return (
              <div key={i} className="inspect-log-row">
                <span className={dotClassName}>
                  <span className="inspect-log-dot-core" />
                </span>
                <div className="inspect-log-content">
                  <div className="inspect-log-head">
                    <div className="inspect-log-heading">
                      {headingChildren}
                      <span className="inspect-log-tail">
                        {lastItem}
                        {byUnit}
                        {actor}
                      </span>
                    </div>
                    <span className="inspect-log-at">
                      {formatActivityTimestamp(when, true)}
                    </span>
                  </div>
                  {verbDetail && (
                    <div className="inspect-log-note">{verbDetail}</div>
                  )}
                  {renderEntryBody(entry, t)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SlateDialog>
  );
}

ActivityLogDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  entries: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  loading: PropTypes.bool,
  error: PropTypes.string,
  currentResultClassName: PropTypes.string,
  imagesLinks: PropTypes.object,
  t: PropTypes.func.isRequired,
};
